<template>
  <div class="report-page">
    <div class="report-scroll">
    <section class="report-card report-toolbar-card">
      <div class="toolbar-top">
        <div class="quick-actions">
          <el-button
            v-for="item in quickRangeOptions"
            :key="item.value"
            size="small"
            :type="quickRange === item.value ? 'primary' : 'default'"
            :plain="quickRange !== item.value"
            @click="handleQuickRangeChange(item.value)"
          >
            {{ item.label }}
          </el-button>
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            unlink-panels
            value-format="timestamp"
            range-separator="-"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            class="toolbar-date"
            @change="handleCustomDateChange"
          />
        </div>

        <div class="toolbar-actions">
          <el-select
            v-model="chartMode"
            size="small"
            placeholder="选择对比方式"
            class="compare-mode-select"
          >
            <el-option label="分天对比" value="compare" />
            <el-option label="时间趋势" value="trend" />
          </el-select>
          <el-button
            size="small"
            icon="el-icon-refresh"
            :loading="loading"
            @click="fetchReport"
          >
            刷新
          </el-button>
          <el-button
            size="small"
            type="primary"
            icon="el-icon-download"
            :disabled="!activeRows.length"
            @click="handleExport"
          >
            导出报表
          </el-button>
        </div>
      </div>

      <div class="toolbar-filters">
        <div class="filter-label">
          <span>筛选</span>
          <i class="el-icon-edit-outline"></i>
        </div>

        <div class="filter-grid filter-grid-hourly">
          <el-select
            v-model="selectedApps"
            filterable
            multiple
            collapse-tags
            placeholder="应用：请选择"
            class="toolbar-field toolbar-field--compact"
          >
            <el-option
              v-for="item in appOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>

          <el-input
            v-model.trim="filters.placement_id_list"
            clearable
            placeholder="广告位：请输入广告位ID"
            class="toolbar-field toolbar-field--compact"
          />

          <el-select
            v-model="selectedAdFormats"
            filterable
            multiple
            collapse-tags
            placeholder="广告类型：请选择"
            class="toolbar-field toolbar-field--compact"
          >
            <el-option
              v-for="item in adTypeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>

          <el-select
            v-model="filters.currency"
            placeholder="币种"
            class="toolbar-field toolbar-field--compact"
          >
            <el-option label="USD" value="USD" />
            <el-option label="CNY" value="CNY" />
          </el-select>

          <el-select
            v-model="filters.time_zone"
            placeholder="时区"
            class="toolbar-field toolbar-field--compact"
          >
            <el-option label="UTC+8" value="UTC+8" />
            <el-option label="UTC+0" value="UTC+0" />
            <el-option label="UTC-8" value="UTC-8" />
          </el-select>
        </div>
      </div>

      <!-- <div class="toolbar-preview">
        <div class="preview-title">数据预览</div>
        <div class="preview-tools">
          <button class="preview-icon is-active">
            <i class="el-icon-data-analysis"></i>
          </button>
          <button class="preview-icon">
            <i class="el-icon-pie-chart"></i>
          </button>
          <button class="preview-icon">
            <i class="el-icon-s-marketing"></i>
          </button>
          <el-switch
            v-model="showDataShadow"
            active-text="显示数据阴影"
            inactive-text=""
          />
        </div>
      </div> -->

      <div class="toolbar-footer">
        <div class="tag-label metric-label">
          <span>缓存</span>
          <el-switch v-model="queryForm.force_refresh" />
        </div>

        <div class="footer-actions">
          <el-button size="small" @click="handleReset">重置</el-button>
          <el-button
            type="primary"
            size="small"
            :loading="loading"
            @click="handleSearch"
          >
            查询
          </el-button>
        </div>
      </div>
    </section>

    <report-meta-bar :meta="metaInfo" class="meta-card" />

    <section class="report-card chart-card">
      <div ref="chartRef" class="chart-box"></div>
    </section>

    <section ref="tableCard" class="report-card table-card">
      <el-tabs v-model="activeTab">
        <el-tab-pane label="分时明细" name="detail"></el-tab-pane>
        <el-tab-pane label="分时汇总" name="summary"></el-tab-pane>
      </el-tabs>

      <div ref="tableShell" class="table-shell">
        <el-table
          ref="reportTable"
          key="report-desktop"
          :data="activeRows"
          :height="tableHeight"
          border
          stripe
          v-loading="loading"
          empty-text="暂无报表数据"
          class="report-table"
        >
          <el-table-column
            v-for="column in activeColumns"
            :key="column.key"
            :prop="column.key"
            :label="column.label"
            :min-width="column.minWidth"
            :width="getColumnWidth(column.key)"
            :fixed="getColumnFixed(column.key)"
            show-overflow-tooltip
          >
            <template slot-scope="{ row }">
              {{ formatCell(column.key, row[column.key]) }}
            </template>
          </el-table-column>
        </el-table>
      </div>
    </section>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts";
import { mapState } from "vuex";
import ReportMetaBar from "./ReportMetaBar.vue";
import {
  buildAppFilterOptions,
  buildHourlyChartData,
  buildHourlyDetailColumns,
  buildHourlySummaryColumns,
  buildHourlySummaryRows,
  buildInclusiveDateList,
  downloadCsv,
  formatDateToYYYYMMDD,
  formatReportValue,
  getDateRangeByShortcut,
  sortHourlyRows,
} from "@/utils/report";

function createDefaultState() {
  const [start, end] = getDateRangeByShortcut("last3");
  return {
    quickRange: "last3",
    dateRange: [start.getTime(), end.getTime()],
    chartMode: "compare",
    activeTab: "detail",
    filters: {
      app_id_list: "",
      placement_id_list: "",
      adformat_list: "",
      currency: "USD",
      time_zone: "UTC+8",
    },
    queryForm: {
      force_refresh: false,
    },
    selectedMetric: "estimated_revenue",
    selectedApps: [],
    selectedAdFormats: [],
  };
}

export default {
  name: "ReportHourly",
  components: {
    ReportMetaBar,
  },
  data() {
    const initialState = createDefaultState();
    return {
      loading: false,
      quickRange: initialState.quickRange,
      dateRange: initialState.dateRange,
      chartMode: initialState.chartMode,
      activeTab: initialState.activeTab,
      filters: initialState.filters,
      queryForm: initialState.queryForm,
      selectedMetric: initialState.selectedMetric,
      selectedApps: initialState.selectedApps,
      selectedAdFormats: initialState.selectedAdFormats,
      showDataShadow: false,
      rows: [],
      detailColumns: [],
      summaryRows: [],
      summaryColumns: [],
      metaInfo: {},
      chartInstance: null,
      tableHeight: 320,
      resizeObserver: null,
    };
  },
  computed: {
    ...mapState({
      appList: (state) => state.appList,
    }),
    quickRangeOptions() {
      return [
        { value: "today", label: "今天" },
        { value: "last3", label: "过去3天" },
        { value: "last7", label: "过去7天" },
        { value: "last30", label: "过去30天" },
      ];
    },
    appOptions() {
      return buildAppFilterOptions(this.appList);
    },
    adTypeOptions() {
      return [
        { value: 0, label: "原生" },
        { value: 1, label: "激励视频" },
        { value: 2, label: "横幅" },
        { value: 3, label: "插屏" },
        { value: 4, label: "开屏" },
      ];
    },
    activeRows() {
      return this.activeTab === "summary" ? this.summaryRows : this.rows;
    },
    activeColumns() {
      return this.activeTab === "summary"
        ? this.summaryColumns
        : this.detailColumns;
    },
  },
  methods: {
    buildRequestParams() {
      const [start_date, end_date] = this.dateRange || [];
      return {
        start_date,
        end_date,
        ...this.filters,
        app_id_list: this.selectedApps,
        adformat_list: this.selectedAdFormats,
        force_refresh: this.queryForm.force_refresh,
        page: 1,
        pageSize: 100,
      };
    },
    async fetchReport() {
      this.loading = true;
      try {
        const payload = await this.$api.getReport.getHourlyReport(
          this.buildRequestParams()
        );
        this.rows = sortHourlyRows(
          Array.isArray(payload.data) ? payload.data : []
        );
        this.metaInfo = payload || {};
        this.detailColumns = buildHourlyDetailColumns(this.rows);
        this.summaryRows = buildHourlySummaryRows(this.rows);
        this.summaryColumns = buildHourlySummaryColumns(this.summaryRows);
        this.renderChart();
        this.syncTableLayout();
      } finally {
        this.loading = false;
      }
    },
    handleQuickRangeChange(value) {
      this.quickRange = value;
      const [start, end] = getDateRangeByShortcut(value);
      this.dateRange = [start.getTime(), end.getTime()];
    },
    handleCustomDateChange() {
      this.quickRange = "";
    },
    validateDateRange() {
      if (!this.dateRange || this.dateRange.length !== 2) {
        this.$message.warning("请选择完整日期区间");
        return false;
      }
      if (this.chartMode === "compare") {
        const days = buildInclusiveDateList(
          this.dateRange[0],
          this.dateRange[1]
        );
        if (days.length > 5) {
          this.$message.warning("分天对比模式最多支持 5 个独立日期");
          return false;
        }
      }
      return true;
    },
    handleSearch() {
      if (!this.validateDateRange()) {
        return;
      }
      this.fetchReport();
    },
    handleReset() {
      const initialState = createDefaultState();
      this.quickRange = initialState.quickRange;
      this.dateRange = initialState.dateRange;
      this.chartMode = initialState.chartMode;
      this.activeTab = initialState.activeTab;
      this.filters = initialState.filters;
      this.queryForm = initialState.queryForm;
      this.selectedMetric = initialState.selectedMetric;
      this.selectedApps = this.appOptions.map((item) => item.value);
      this.selectedAdFormats = initialState.selectedAdFormats;
      this.showDataShadow = false;
      this.fetchReport();
    },
    ensureChart() {
      if (!this.chartInstance) {
        this.chartInstance = echarts.init(this.$refs.chartRef);
      }
      return this.chartInstance;
    },
    renderChart() {
      const chart = this.ensureChart();
      const chartData = buildHourlyChartData(
        this.rows,
        this.chartMode,
        this.selectedMetric
      );
      chart.setOption({
        tooltip: {
          trigger: "axis",
        },
        legend: {
          data: chartData.legend,
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: chartData.xAxis,
        },
        yAxis: {
          type: "value",
        },
        series: chartData.series,
      });
      chart.resize();
      this.syncTableLayout();
    },
    formatCell(key, value) {
      return formatReportValue(key, value, this.metaInfo);
    },
    syncTableLayout() {
      this.$nextTick(() => {
        window.requestAnimationFrame(() => {
          const shell = this.$refs.tableShell;
          const table = this.$refs.reportTable;
          if (shell) {
            this.tableHeight = Math.max(shell.clientHeight, 180);
          }
          if (table && typeof table.doLayout === "function") {
            table.doLayout();
          }
        });
      });
    },
    bindTableObserver() {
      this.syncTableLayout();
      if (typeof window !== "undefined" && "ResizeObserver" in window) {
        this.resizeObserver = new window.ResizeObserver(() => {
          this.syncTableLayout();
          if (this.chartInstance) this.chartInstance.resize();
        });
        if (this.$refs.tableCard) {
          this.resizeObserver.observe(this.$refs.tableCard);
        }
        if (this.$refs.tableShell) {
          this.resizeObserver.observe(this.$refs.tableShell);
        }
        if (this.$refs.chartRef) {
          this.resizeObserver.observe(this.$refs.chartRef);
        }
      }
      window.addEventListener("resize", this.syncTableLayout);
    },
    unbindTableObserver() {
      window.removeEventListener("resize", this.syncTableLayout);
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
    },
    getColumnFixed(key) {
      if (
        key === "date" ||
        key === "hour" ||
        key === "app_name" ||
        key === "app_id"
      ) {
        return "left";
      }
      return false;
    },
    getColumnWidth(key) {
      if (key === "date") {
        return 120;
      }
      if (key === "hour") {
        return 120;
      }
      if (key === "app_name") {
        return 180;
      }
      if (key === "app_id") {
        return 120;
      }
      return undefined;
    },
    handleExport() {
      const dateText = formatDateToYYYYMMDD(Date.now());
      downloadCsv(
        `hourly-report-${this.activeTab}-${dateText}.csv`,
        this.activeColumns,
        this.activeRows,
        this.metaInfo
      );
    },
  },
  watch: {
    chartMode() {
      this.$nextTick(() => {
        this.renderChart();
      });
    },
    activeTab() {
      this.syncTableLayout();
    },
  },
  created() {
    this.selectedApps = this.appOptions.map((item) => item.value);
    this.fetchReport();
  },
  mounted() {
    window.addEventListener("resize", this.renderChart);
    this.bindTableObserver();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.renderChart);
    this.unbindTableObserver();
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
  },
};
</script>

<style lang="less" scoped>
.report-page {
  min-height: 0;
  height: 100%;
  overflow: hidden;
  box-sizing: border-box;
}

.report-scroll {
  height: 100%;
  min-height: 0;
  padding-right: 4px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
}

.report-card {
  padding: 20px;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 14px 28px rgba(30, 85, 187, 0.08);
}

.report-toolbar-card {
  padding: 16px 18px 18px;
}

.toolbar-top,
.toolbar-footer,
.toolbar-preview {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.toolbar-top {
  margin-bottom: 14px;
}

.quick-actions,
.toolbar-actions,
.preview-tools {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.toolbar-date {
  width: 280px;
}

.compare-mode-select {
  width: 140px;
}

.toolbar-filters {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.filter-label,
.tag-label,
.preview-title {
  min-width: 48px;
  color: #4f6389;
  font-size: 13px;
  font-weight: 600;
}

.filter-grid {
  flex: 1;
  display: grid;
  gap: 12px;
}

.filter-grid-hourly {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.toolbar-field {
  width: 100%;
}

.toolbar-field--compact /deep/ .el-input__inner,
.toolbar-field--compact /deep/ .el-select__tags {
  font-size: 12px;
}

.toolbar-field--compact /deep/ .el-input__inner {
  height: 34px;
  line-height: 34px;
}

.toolbar-field--compact /deep/ .el-tag {
  max-width: 90px;
}

.toolbar-preview,
.toolbar-tags,
.toolbar-footer {
  margin-top: 16px;
}

.preview-icon {
  width: 32px;
  height: 32px;
  border: 1px solid #dde6f5;
  border-radius: 8px;
  background: #fff;
  color: #6a84b3;
  cursor: pointer;
}

.preview-icon.is-active {
  color: #2f7dec;
  border-color: #c5dbff;
  background: #eef5ff;
}

.tag-row {
  display: flex;
  gap: 16px;
  align-items: flex-start;
}

.tag-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.tag-chip {
  border: 1px solid #dde6f5;
  background: #fff;
  color: #5b7096;
  border-radius: 8px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tag-chip.is-active {
  color: #2f7dec;
  border-color: #c5dbff;
  background: #eef5ff;
}

.footer-actions {
  display: flex;
  gap: 10px;
}

.meta-card {
  margin-top: 0;
}

.chart-card {
  padding-bottom: 10px;
  flex: none;
  overflow: hidden;
}

.table-card {
  display: flex;
  flex-direction: column;
  flex: none;
  height: 420px;
  min-height: 420px;
  overflow: hidden;
}

.table-shell {
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.chart-box {
  width: 100%;
  height: 420px;
}

.table-card /deep/ .el-tabs {
  display: flex;
  flex-direction: column;
  // flex: 1;
  min-height: 0;
}

.table-card /deep/ .el-tabs__content {
  display: none;
}

.table-card /deep/ .el-table {
  width: 100%;
}

.table-card /deep/ .el-table__body-wrapper,
.table-card /deep/ .el-table__fixed-body-wrapper {
  overflow: auto !important;
}

@media (max-width: 1280px) {
  .filter-grid-hourly {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

</style>
